import { Box, Grid, TextField } from '@atgfr8/atg-ui-tailwind';
import { Button, ToastType } from '@components';
import { createForm } from '@felte/solid';
import { useParams } from '@solidjs/router';
import {
  creditIncrease,
  creditRequestStore,
  setCreditRequestStore,
} from '@store/customers/customerDetails';
import { closeModal } from '@store/modals';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { handleToast } from '@utils/utils';
import { onMount, Show } from 'solid-js';

type RequestMoreCreditProps = {
  modalId: string;
  handleClose?: () => void;
  customerId?: string;
  onSubmitSuccess?: () => void;
  showCancel?: boolean;
  dataTestIdPrefix?: string;
};

type MoreCreditModalFormState = {
  amount: number;
  reason: string;
};

const RequestMoreCredit = (props: RequestMoreCreditProps) => {
  const params = useParams();
  const prefix = () => props.dataTestIdPrefix ?? 'request-more-credit';

  const { form, reset, data, setFields } = createForm<MoreCreditModalFormState>(
    {
      initialValues: {
        amount: creditRequestStore.creditRequest.amount || 0,
        reason: creditRequestStore.creditRequest.reason || '',
      },
      onSubmit: async (values) => {
        await handleSubmission(values);
      },
    },
  );

  onMount(() => {
    setFields('amount', 0);
    setFields('reason', '');
    setCreditRequestStore('creditRequest', {});
  });

  const handleSubmission = async (values: MoreCreditModalFormState) => {
    const resolvedCustomerId =
      props.customerId ?? (params.id as unknown as string);

    await creditIncrease({ ...values, customerId: resolvedCustomerId }, () => {
      setCreditRequestStore('creditRequest', {});
      closeModal(props.modalId);
      reset();
      handleToast(ToastType.Success, 'Credit request was submitted');
      props.onSubmitSuccess?.();
    });
  };

  const preventingNonNumeric = (e: KeyboardEvent) => {
    if (e.key === 'e' || e.key === '-' || e.key === '+') {
      e.preventDefault();
    }
  };

  return (
    <form ref={form}>
      <Box
        style={{
          'flex-grow': 1,
        }}
        pt={1}
      >
        <Grid container spacing={2}>
          <Grid item size={12}>
            <Grid container spacing={2}>
              <Grid item size={12}>
                <TextField
                  label="Amount"
                  type="number"
                  placeholder="Enter Amount here"
                  value={data().amount}
                  onChange={(e: unknown) => {
                    const value = Number(e as number);
                    setFields('amount', value);
                    setCreditRequestStore('creditRequest', 'amount', value);
                  }}
                  startIcon="$"
                  maxLength={10}
                  onKeyPress={preventingNonNumeric}
                  dataTestId={`${prefix()}-amount-textInput`}
                />
              </Grid>
              <Grid item size={12}>
                <TextField
                  label="Reason/Comment"
                  placeholder="Enter Comment here"
                  value={data().reason}
                  onChange={(e: string) => {
                    setFields('reason', e);
                    setCreditRequestStore('creditRequest', 'reason', e);
                  }}
                  maxLength={4000}
                  dataTestId={`${prefix()}-reason-textInput`}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item size={12} justifyContent="end" display="flex">
            <Box class="flex gap-2">
              <Show when={props.showCancel}>
                <Button
                  label="Cancel"
                  variant="outlined"
                  onClick={() => {
                    reset();
                    setCreditRequestStore('creditRequest', {});
                    closeModal(props.modalId);
                  }}
                  dataTestId={formatDataTestId(`${prefix()}-cancel`, 'button')}
                />
              </Show>
              <Button
                label="Submit Request"
                disabled={!Boolean(data().amount)}
                type="submit"
                variant="contained"
                color="primary"
                isLoading={creditRequestStore.isLoading}
                dataTestId={formatDataTestId(`${prefix()}-submit`, 'button')}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
    </form>
  );
};

export default RequestMoreCredit;
